/**
 * DecayHeatmap — W46 Custom SVG heatmap.
 * Axes: days-since-last-touch (X) vs deal value (Y).
 * Color intensity = red spectrum (more accounts = deeper red).
 * Style: OPTION B Custom SVG. No Recharts.
 */

import React, { useState } from "react";

export interface DecayAccount {
  name: string;
  daysSince: number;
  dealValue: number;
}

const DAY_BUCKETS = [
  { label: "0-7d",    min: 0,   max: 7    },
  { label: "8-14d",   min: 8,   max: 14   },
  { label: "15-30d",  min: 15,  max: 30   },
  { label: "31-60d",  min: 31,  max: 60   },
  { label: "61-90d",  min: 61,  max: 90   },
  { label: "90d+",    min: 91,  max: 9999 },
];

const VALUE_BUCKETS = [
  { label: ">$1M",       min: 1_000_000, max: Infinity },
  { label: "$500K-$1M",  min: 500_000,   max: 999_999  },
  { label: "$250K-$500K",min: 250_000,   max: 499_999  },
  { label: "$100K-$250K",min: 100_000,   max: 249_999  },
  { label: "<$100K",     min: 0,         max: 99_999   },
];

function dayBucket(days: number) {
  for (let i = 0; i < DAY_BUCKETS.length; i++) {
    if (days >= DAY_BUCKETS[i].min && days <= DAY_BUCKETS[i].max) return i;
  }
  return DAY_BUCKETS.length - 1;
}

function valueBucket(val: number) {
  for (let i = 0; i < VALUE_BUCKETS.length; i++) {
    if (val >= VALUE_BUCKETS[i].min && val <= VALUE_BUCKETS[i].max) return i;
  }
  return VALUE_BUCKETS.length - 1;
}

function intensityColor(count: number, max: number): string {
  if (count === 0) return "#1F2937";
  const t = Math.min(1, count / Math.max(1, max));
  if (t < 0.25) return "rgba(127,29,29,0.2)";
  if (t < 0.5)  return "rgba(153,27,27,0.5)";
  if (t < 0.75) return "rgba(220,38,38,0.7)";
  return "#DC2626";
}

const CELL_W = 52;
const CELL_H = 28;
const LEFT_PAD = 82;
const TOP_PAD = 26;

export function DecayHeatmap({ accounts }: { accounts: DecayAccount[] }) {
  const [hovered, setHovered] = useState<{ d: number; v: number } | null>(null);

  const grid: number[][] = Array.from({ length: VALUE_BUCKETS.length }, () =>
    new Array(DAY_BUCKETS.length).fill(0));
  const cellNames: string[][][] = Array.from({ length: VALUE_BUCKETS.length }, () =>
    Array.from({ length: DAY_BUCKETS.length }, () => []));

  for (const acc of accounts) {
    const db = dayBucket(acc.daysSince);
    const vb = valueBucket(acc.dealValue);
    grid[vb][db]++;
    cellNames[vb][db].push(acc.name);
  }

  const maxCount = Math.max(...grid.flat(), 1);
  const svgW = LEFT_PAD + DAY_BUCKETS.length * CELL_W + 8;
  const svgH = TOP_PAD + VALUE_BUCKETS.length * CELL_H + 22;

  return (
    <div className="w-full overflow-x-auto">
      <svg viewBox={`0 0 ${svgW} ${svgH}`} className="w-full"
        style={{ minWidth: 340, maxHeight: 220 }}
        preserveAspectRatio="xMinYMin meet"
        role="img" aria-label="Account decay heatmap">
        {DAY_BUCKETS.map((db, di) => (
          <text key={di} x={LEFT_PAD + di * CELL_W + CELL_W / 2} y={TOP_PAD - 6}
            fontSize="8" textAnchor="middle" fill="#6B7280" fontFamily="inherit">
            {db.label}
          </text>
        ))}
        {VALUE_BUCKETS.map((vb, vi) => (
          <g key={vi}>
            <text x={LEFT_PAD - 4} y={TOP_PAD + vi * CELL_H + CELL_H / 2 + 4}
              fontSize="8" textAnchor="end" fill="#6B7280" fontFamily="inherit">
              {vb.label}
            </text>
            {DAY_BUCKETS.map((_, di) => {
              const count = grid[vi][di];
              const isHov = hovered?.d === di && hovered?.v === vi;
              return (
                <g key={di} onMouseEnter={() => setHovered({ d: di, v: vi })}
                  onMouseLeave={() => setHovered(null)}
                  style={{ cursor: count > 0 ? "pointer" : "default" }}>
                  <rect x={LEFT_PAD + di * CELL_W + 1} y={TOP_PAD + vi * CELL_H + 1}
                    width={CELL_W - 2} height={CELL_H - 2} rx="2"
                    fill={intensityColor(count, maxCount)}
                    stroke={isHov ? "#fff" : "#374151"} strokeWidth={isHov ? 1.5 : 0.5} />
                  {count > 0 && (
                    <text x={LEFT_PAD + di * CELL_W + CELL_W / 2}
                      y={TOP_PAD + vi * CELL_H + CELL_H / 2 + 4}
                      fontSize="10" fontWeight="600" textAnchor="middle"
                      fill="#fff" fontFamily="inherit">{count}</text>
                  )}
                  {count > 0 && (
                    <title>
                      {`${vb.label} / ${DAY_BUCKETS[di].label}: ${count} account${count !== 1 ? "s" : ""}`}
                      {cellNames[vi][di].length <= 3 ? `\n${cellNames[vi][di].join(", ")}` : ""}
                    </title>
                  )}
                </g>
              );
            })}
          </g>
        ))}
        <text x={LEFT_PAD + (DAY_BUCKETS.length * CELL_W) / 2} y={svgH - 2}
          fontSize="9" textAnchor="middle" fill="#6B7280" fontFamily="inherit">
          Days since last touch
        </text>
      </svg>
    </div>
  );
}
